import { apiPost, apiPostSession } from '../client';
import { IS_TEST_MODE } from '../../config/env';
import type {
  CurrentUser,
  ForgotPasswordRequest,
  LoginRequest,
  SessionResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
} from '../types/auth';
import type { ResponseBase } from '../types/common';

export function login(
  username: string,
  password: string,
  recaptchaToken?: string
): Promise<SessionResponse> {
  const body: LoginRequest = {
    username,
    password,
    // No server-side context to read the client's real IP from in a pure SPA;
    // left blank until/unless the backend is confirmed to require it.
    ipAddress: '',
    isTest: IS_TEST_MODE,
    isEncryptedPassword: false,
    recaptchaToken,
  };
  return apiPost<LoginRequest, SessionResponse>('Login', body, { skipSession: true });
}

/**
 * OTP / 2FA (backend Faz G). CONFIRMED live against swagger 2026-08-27: `Session/Verify`
 * `{ sessionId, code }` (bare body, no session header) → `{ verified, sessionId,
 * sessionToken }`; `Session/ResendOtp` `{ sessionId }`; `Login` now returns
 * `{ requiresOtp, otpMaskedEmail }` and accepts `recaptchaToken`. The API was already
 * generating the OTP code on Login — only verify/resend were missing.
 */
export function verifyOtp(body: VerifyOtpRequest): Promise<VerifyOtpResponse> {
  return apiPost<VerifyOtpRequest, VerifyOtpResponse>('Session/Verify', body, { skipSession: true });
}

export function resendOtp(sessionId: string): Promise<ResponseBase> {
  return apiPost<{ sessionId: string }, ResponseBase>('Session/ResendOtp', { sessionId }, {
    skipSession: true,
  });
}

export function fetchCurrentUser(): Promise<CurrentUser> {
  return apiPostSession<CurrentUser>('Authenticated/Current');
}

/**
 * "Şifremi Unuttum" — the three Session/* routes are named the OPPOSITE of what they do
 * (confirmed live + fixed backend-side, F-11 2026-09-01). Real behaviour:
 *   - Session/ResetPassword      { username }                                → generates a
 *       token and e-mails a link to `/forgot-password?token=…`. Generic response (no
 *       token/mail in the body), rate-limited (5 / 15 min per IP + username).
 *   - Session/ValidateResetToken { token }                                   → checks the
 *       token is known and inside its 1-hour window.
 *   - Session/ForgotPassword     { token, newPassword, confirmPassword }      → writes the
 *       new password (server enforces min 6 + match). No username needed — the user is
 *       resolved from the token.
 * Wrapped below under intent-revealing names so callers aren't misled by the route names.
 */
export function requestPasswordReset(username: string): Promise<ResponseBase> {
  return apiPost<ForgotPasswordRequest, ResponseBase>(
    'Session/ResetPassword',
    { username },
    { skipSession: true, timeoutMs: 240_000 }
  );
}

export function validateResetToken(token: string): Promise<ResponseBase> {
  return apiPost<ForgotPasswordRequest, ResponseBase>(
    'Session/ValidateResetToken',
    { token },
    { skipSession: true }
  );
}

export function submitNewPassword(body: {
  token: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<ResponseBase> {
  return apiPost<ForgotPasswordRequest, ResponseBase>('Session/ForgotPassword', body, {
    skipSession: true,
    timeoutMs: 240_000,
  });
}
